import type { Viewport } from "./types";

const VIEWPORT_KEY = "wheretopee.lastViewport.v1";
const DEFAULT_VIEWPORT: Viewport = { lat: 43.7696, lon: 11.2558, zoom: 14 }; // Florence centro
// Mobile users often need a few seconds to read the OS-level prompt; keep generous.
const GEO_TIMEOUT_MS = 12_000;

export type GeoOutcome =
  | { kind: "geolocation"; coords: { lat: number; lon: number }; zoom: 16 }
  | { kind: "last-known"; viewport: Viewport }
  | { kind: "needs-search"; defaultViewport: Viewport };

export function loadLastViewport(): Viewport | null {
  try {
    const raw = window.localStorage.getItem(VIEWPORT_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      typeof (parsed as Viewport).lat === "number" &&
      typeof (parsed as Viewport).lon === "number" &&
      typeof (parsed as Viewport).zoom === "number"
    ) {
      return parsed as Viewport;
    }
  } catch {
    /* fall through */
  }
  return null;
}

export function saveViewport(v: Viewport): void {
  try {
    window.localStorage.setItem(VIEWPORT_KEY, JSON.stringify(v));
  } catch {
    /* ignore */
  }
}

function tryGeolocation(): Promise<{ lat: number; lon: number } | null> {
  if (!("geolocation" in navigator)) return Promise.resolve(null);
  return new Promise((resolve) => {
    let settled = false;
    const settle = (value: { lat: number; lon: number } | null): void => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const timer = window.setTimeout(() => settle(null), GEO_TIMEOUT_MS);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        window.clearTimeout(timer);
        settle({ lat: pos.coords.latitude, lon: pos.coords.longitude });
      },
      () => {
        window.clearTimeout(timer);
        settle(null);
      },
      { enableHighAccuracy: false, maximumAge: 60_000, timeout: GEO_TIMEOUT_MS },
    );
  });
}

export async function decideInitialViewport(): Promise<GeoOutcome> {
  const coords = await tryGeolocation();
  if (coords) {
    return { kind: "geolocation", coords, zoom: 16 };
  }
  const last = loadLastViewport();
  if (last) {
    return { kind: "last-known", viewport: last };
  }
  return { kind: "needs-search", defaultViewport: DEFAULT_VIEWPORT };
}

export function defaultViewport(): Viewport {
  return DEFAULT_VIEWPORT;
}

export type LocateError = "unsupported" | "permission_denied" | "unavailable" | "timeout";

export interface LocateResult {
  ok: true;
  coords: { lat: number; lon: number };
  accuracy: number;
}

export interface LocateFailure {
  ok: false;
  error: LocateError;
  message: string;
}

// User-initiated geolocation (call this from a click/tap handler so iOS
// reliably shows its permission prompt).
export function requestUserLocation(timeoutMs: number = 15_000): Promise<LocateResult | LocateFailure> {
  if (!navigator.geolocation || typeof navigator.geolocation.getCurrentPosition !== "function") {
    return Promise.resolve({
      ok: false,
      error: "unsupported",
      message: "this browser doesn't support geolocation",
    });
  }
  return new Promise((resolve) => {
    let settled = false;
    const settle = (value: LocateResult | LocateFailure): void => {
      if (settled) return;
      settled = true;
      resolve(value);
    };
    const timer = window.setTimeout(
      () =>
        settle({
          ok: false,
          error: "timeout",
          message: "location request timed out - check your phone's location services",
        }),
      timeoutMs + 500,
    );
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        window.clearTimeout(timer);
        settle({
          ok: true,
          coords: { lat: pos.coords.latitude, lon: pos.coords.longitude },
          accuracy: pos.coords.accuracy,
        });
      },
      (err) => {
        window.clearTimeout(timer);
        let kind: LocateError = "unavailable";
        let message = "couldn't get your location";
        if (err.code === err.PERMISSION_DENIED) {
          kind = "permission_denied";
          message =
            "location permission denied - allow it in Settings → Safari → Location (or per-site settings)";
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          kind = "unavailable";
          message = "your phone couldn't determine its location";
        } else if (err.code === err.TIMEOUT) {
          kind = "timeout";
          message = "location request timed out";
        }
        settle({ ok: false, error: kind, message });
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: timeoutMs },
    );
  });
}
